"use client";

import { createContext, useState, useCallback, SetStateAction } from "react";

import { checkTransact, getReviews } from "~/api/transact";
import { useProfileSSOid } from "~/queries/profile/useProfile";
import {
  ITransactable,
  IPendingReview,
  ITransactableProfile,
} from "~/types/api-types";

interface TransactableContextValue {
  transactableState: string;
  readyToTransact: boolean;
  checkTransactable: (intendedActionCB?: () => void) => void;
  totalPendingReviews: number;
  currentPendingReview: IPendingReview | null;
  pendingReviewsIndex: number;
  next: () => void;
  transactionProcessing: boolean;
  setTransactionProcessing: (processing: SetStateAction<boolean>) => void;
  profileData: ITransactableProfile;
}

const defaultProfileData: ITransactableProfile = {
  _id: "",
  is_instant_book_eligible_host: false,
  SSOid: "",
  email: "",
  phone: "",
  email_verified: false,
  pending_reviews_count: 0,
  first_name: "",
  last_name: "",
  company_name: "",
  job_title: "",
  referral_source: "",
  avatar_id: "",
  avatar_url: "",
  account_id: "",
  phone_country: "",
  phone_formatted: "",
  preferred_locale: "",
  customer_id: "",
  charges_enabled: false,
  payouts_enabled: false,
  host_review_count: 0,
  host_review_stars: 0,
  skipped_reviews_count: 0,
  guest_review_count: 0,
  guest_review_stars: 0,
  manual_payouts: false,
};

export const TransactableContext = createContext<TransactableContextValue>({
  transactableState: "",
  readyToTransact: true,
  checkTransactable: () => {},
  totalPendingReviews: 0,
  currentPendingReview: null,
  pendingReviewsIndex: 0,
  next: () => {},
  transactionProcessing: false,
  setTransactionProcessing: () => {},
  profileData: defaultProfileData,
});

export const TRANSACTABLE_STATES = {
  PENDING_REVIEWS: "Pending Reviews to Complete",
  AVATAR: "Avatar",
  EMAIL_VERIFICATION: "Email Verification",
  FIRST_NAME: "First Name",
  LAST_NAME: "Last Name",
  MISSING_PROFILE_DATA: "Missing Profile Data",
};

const consolidateTransactableStates = (states: string[]): string[] => {
  const PROFILE_DATA_STATES = [
    TRANSACTABLE_STATES.FIRST_NAME,
    TRANSACTABLE_STATES.LAST_NAME,
    TRANSACTABLE_STATES.AVATAR,
  ];
  const hasProfileDataStates = states.some((state) =>
    PROFILE_DATA_STATES.includes(state),
  );
  const nonProfileStates = states.filter(
    (state) => !PROFILE_DATA_STATES.includes(state),
  );
  return hasProfileDataStates
    ? [TRANSACTABLE_STATES.MISSING_PROFILE_DATA, ...nonProfileStates]
    : states;
};

export const TransactableProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [transactionProcessing, setTransactionProcessing] = useState(false);
  const [readyToTransact, setReadyToTransact] = useState(false);
  const [transactableState, setTransactableState] = useState<string>("");
  const [missingData, setMissingData] = useState<string[]>([]);
  const [missingDataIndex, setMissingDataIndex] = useState<number>(0);
  const [profileData, setProfileData] = useState<ITransactableProfile | null>(
    null,
  );
  const [pendingReviews, setPendingReviews] = useState<IPendingReview[]>([]);
  const [pendingReviewsIndex, setPendingReviewsIndex] = useState<number>(0);
  const [totalPendingReviews, setTotalPendingReviews] = useState<number>(0);
  const [intendedAction, setIntendedAction] = useState<() => void>(() => {});
  const [currentPendingReview, setCurrentPendingReview] =
    useState<IPendingReview | null>(null);
  const SSOId = useProfileSSOid() || "";

  const fetchPendingReviews = useCallback(async () => {
    const pendingReviews: IPendingReview[] = await getReviews(SSOId);
    setPendingReviews(pendingReviews);
    setTotalPendingReviews(pendingReviews.length);
    setPendingReviewsIndex(0);
    setCurrentPendingReview(pendingReviews[0]);
  }, [SSOId]);

  const checkTransactable = async (intendedActionCB?: () => void) => {
    setTransactionProcessing(true);
    if (intendedActionCB) {
      setIntendedAction(() => intendedActionCB);
    }
    try {
      const transact: ITransactable = await checkTransact(SSOId);
      setReadyToTransact(transact.ready_to_transact);
      if (transact.ready_to_transact) {
        if (intendedActionCB) {
          intendedActionCB();
        } else if (intendedAction) {
          intendedAction();
        }
        setTransactionProcessing(false);
        return;
      }
      if (transact.missing) {
        window.analyticsIntegrations.transactableViewed(transact.missing);
        const consolidatedMissingData = consolidateTransactableStates(
          transact.missing,
        );
        setProfileData(transact.profile);
        setMissingData(consolidatedMissingData);
        setMissingDataIndex(0);
        setTransactableState(consolidatedMissingData[0]);
        if (transact.missing[0] === TRANSACTABLE_STATES.PENDING_REVIEWS) {
          fetchPendingReviews();
        }
      }
    } catch (error) {
      setTransactionProcessing(false);
      throw new Error("Error in checking transaction state", { cause: error });
    }
  };

  const next = () => {
    if (transactableState === TRANSACTABLE_STATES.PENDING_REVIEWS) {
      const nextPendingReviewIndex = pendingReviewsIndex + 1;
      if (nextPendingReviewIndex < pendingReviews.length) {
        setPendingReviewsIndex(nextPendingReviewIndex);
        setCurrentPendingReview(pendingReviews[nextPendingReviewIndex]);
        return;
      }
      setPendingReviewsIndex(0);
      setPendingReviews([]);
      setTotalPendingReviews(0);
      setCurrentPendingReview(null);
    }
    const nextMissingDataIndex = missingDataIndex + 1;
    setMissingDataIndex(nextMissingDataIndex);
    setTransactableState(missingData[nextMissingDataIndex]);
    if (nextMissingDataIndex >= missingData.length) {
      checkTransactable();
    }
  };

  return (
    <TransactableContext.Provider
      value={{
        readyToTransact,
        checkTransactable,
        totalPendingReviews,
        currentPendingReview,
        pendingReviewsIndex,
        transactableState,
        next,
        transactionProcessing,
        setTransactionProcessing,
        profileData: profileData || defaultProfileData,
      }}
    >
      {children}
    </TransactableContext.Provider>
  );
};
